import {z} from 'zod';
import {requisitionDate} from './requisitions';
import {amountToMinor,minorToAmount} from './documentPricing';
import {receiptListQuerySchema} from './warehouseReceipts';
/**
 * `ClaimRecovery` (17/09/2026): tài khoản ghi CÓ khi phiếu ghi nợ nhà cung cấp được ghi sổ — khoản phạt /
 * bồi thường thu từ nhà cung cấp. Theo chế độ kế toán Việt Nam thường là 711 Thu nhập khác; bản đồ
 * ghi sổ quyết định, không mã cứng ở đây.
 */
export const glRoleSchema=z.enum(['Expense','Payable','Cash','TaxRecoverable','TaxPayable','ClaimRecovery']);
export const glSourceKindSchema=z.enum(['ApInvoice','ApPayment','ApDebitNote']);
export type GlRole=z.infer<typeof glRoleSchema>;
export type GlSourceKind=z.infer<typeof glSourceKindSchema>;
export const glMapSchema=z.object({sourceKind:glSourceKindSchema,role:glRoleSchema,accountCode:z.string().trim().min(1).max(32)});
export type GlMap=z.infer<typeof glMapSchema>;
export const accountingPolicyDraftSchema=z.object({
 requestId:z.string().uuid(),functionalCurrency:z.string().trim().regex(/^[A-Za-z]{3}$/).transform(v=>v.toUpperCase()),
 basis:z.string().trim().min(5).max(1000),maps:z.array(glMapSchema).min(1).max(24),
}).superRefine((v,ctx)=>{
 // Hoá đơn ghi CÓ, phiếu chi và phiếu ghi nợ ghi NỢ cùng một khoản phải trả người bán (như tài khoản đối
 // chiếu nhà cung cấp của SAP). Hai tài khoản khác nhau thì hoá đơn "đã trả / đã trừ" mà sổ cái không về 0.
 if(new Set(v.maps.filter(m=>m.role==='Payable').map(m=>m.accountCode)).size>1)ctx.addIssue({code:z.ZodIssueCode.custom,message:'gl_payable_mismatch',path:['maps']});
});
export const accountingPolicyActionSchema=z.object({revision:z.number().int().positive(),action:z.enum(['activate','retire']),reason:z.string().trim().min(5).max(1000)});
export const accountingPeriodSchema=z.object({requestId:z.string().uuid(),period:z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),status:z.enum(['Open','Closed']),basis:z.string().trim().min(5).max(1000)});
export const glAccountDraftSchema=z.object({requestId:z.string().uuid(),code:z.string().trim().min(1).max(32),name:z.string().trim().min(1).max(200),type:z.enum(['Asset','Liability','Equity','Revenue','Expense'])});
export const apLineSchema=z.object({id:z.string().uuid(),poLineId:z.string().min(1).max(100),quantity:z.string().trim().regex(/^(?:0|[1-9]\d{0,7})(?:\.\d{1,4})?$/),amount:z.string().trim().regex(/^(?:0|[1-9]\d{0,11})(?:\.\d{1,2})?$/)});
export const apInvoiceDraftSchema=z.object({
 requestId:z.string().uuid(),poId:z.string().min(1).max(100),invoiceDate:requisitionDate,taxAmount:z.string().trim().regex(/^(?:0|[1-9]\d{0,11})(?:\.\d{1,2})?$/).default('0'),
 notes:z.string().trim().max(2000).default(''),lines:z.array(apLineSchema).min(1).max(50),
});
export const apPaymentDraftSchema=z.object({
 requestId:z.string().uuid(),supplierId:z.string().min(1).max(100),paidDate:requisitionDate,currency:z.string().trim().regex(/^[A-Za-z]{3}$/).transform(v=>v.toUpperCase()),
 amount:z.string().trim().regex(/^(?:0|[1-9]\d{0,11})(?:\.\d{1,2})?$/),notes:z.string().trim().max(2000).default(''),
 allocations:z.array(z.object({id:z.string().uuid(),invoiceId:z.string().uuid(),amount:z.string().trim().regex(/^(?:0|[1-9]\d{0,11})(?:\.\d{1,2})?$/)})).min(1).max(24),
});
export const glActionSchema=z.object({revision:z.number().int().positive(),action:z.enum(['post','reverse']),reason:z.string().trim().min(5).max(1000)});
const money2Input=z.string().trim().regex(/^(?:0|[1-9]\d{0,11})(?:\.\d{1,2})?$/);
/** Ghi sổ phiếu ghi nợ: kế toán chọn NGÀY chứng từ lúc ghi (quyết định kỳ). Đảo dùng ngày của phiếu. */
export const apDebitNoteActionSchema=z.object({revision:z.number().int().positive(),action:z.enum(['post','reverse']),reason:z.string().trim().min(5).max(1000),noteDate:requisitionDate.optional()})
 .superRefine((v,ctx)=>{if(v.action==='post'&&!v.noteDate)ctx.addIssue({code:z.ZodIssueCode.custom,message:'gl_period',path:['noteDate']});});
/**
 * Cấn trừ phiếu ghi nợ vào một hoá đơn nhà cung cấp đã ghi sổ. Mọi quyết định trên phiếu (ghi sổ, đảo, trừ,
 * bỏ trừ) đi CÙNG MỘT đường `POST /finance/debit-notes/:id/actions`: đường kết thúc bằng `actions` là đường
 * quyết định, nên người chỉ có ô Duyệt (không có ô Tạo) vẫn qua được `requireAuth`, rồi `requireDecision`
 * đọc `action` trong thân.
 */
export const apDebitNoteApplySchema=z.object({action:z.literal('apply'),requestId:z.string().uuid(),invoiceId:z.string().min(1).max(100),amount:money2Input,reason:z.string().trim().max(1000).default('')});
export const apDebitNoteUnapplySchema=z.object({action:z.literal('unapply'),applicationId:z.string().min(1).max(100),reason:z.string().trim().min(5).max(1000)});
export type AccountingPolicyInput=z.input<typeof accountingPolicyDraftSchema>;
export type AccountingPeriodInput=z.infer<typeof accountingPeriodSchema>;
export type GlAccountInput=z.infer<typeof glAccountDraftSchema>;
export type ApInvoiceInput=z.input<typeof apInvoiceDraftSchema>;
export type ApPaymentInput=z.infer<typeof apPaymentDraftSchema>;
export type GlAction=z.infer<typeof glActionSchema>;
export type ApDebitNoteAction=z.infer<typeof apDebitNoteActionSchema>;
export type ApDebitNoteApplyInput=z.input<typeof apDebitNoteApplySchema>;
export type ApDebitNoteUnapplyInput=z.infer<typeof apDebitNoteUnapplySchema>;
export interface AccountingPolicyDefinition {maps:GlMap[];}
export function periodKey(date:string):string{
 if(!/^\d{4}-(0[1-9]|1[0-2])-\d{2}$/.test(date))throw Error('gl_period');
 return date.slice(0,7);
}
export function requiredRoles(kind:GlSourceKind,taxMinor:number):GlRole[]{
 if(kind==='ApInvoice')return taxMinor>0?['Expense','Payable','TaxRecoverable']:['Expense','Payable'];
 if(kind==='ApDebitNote')return ['Payable','ClaimRecovery'];
 return ['Payable','Cash'];
}
export function resolveMap(maps:GlMap[],kind:GlSourceKind,role:GlRole):string{
 const hit=maps.find(m=>m.sourceKind===kind&&m.role===role);
 if(!hit)throw Error('gl_unmapped');
 return hit.accountCode;
}
export interface JournalLineDraft {accountCode:string;debitMinor:number;creditMinor:number;}
export function apInvoiceJournal(maps:GlMap[],goodsMinor:number,taxMinor:number):JournalLineDraft[]{
 if(goodsMinor<=0)throw Error('gl_unbalanced');
 const expense=resolveMap(maps,'ApInvoice','Expense'),payable=resolveMap(maps,'ApInvoice','Payable');
 const lines:JournalLineDraft[]=[{accountCode:expense,debitMinor:goodsMinor,creditMinor:0}];
 if(taxMinor>0)lines.push({accountCode:resolveMap(maps,'ApInvoice','TaxRecoverable'),debitMinor:taxMinor,creditMinor:0});
 lines.push({accountCode:payable,debitMinor:0,creditMinor:goodsMinor+taxMinor});
 assertBalanced(lines);return lines;
}
export function apPaymentJournal(maps:GlMap[],amountMinor:number):JournalLineDraft[]{
 if(amountMinor<=0)throw Error('gl_unbalanced');
 const lines:JournalLineDraft[]=[
  {accountCode:resolveMap(maps,'ApPayment','Payable'),debitMinor:amountMinor,creditMinor:0},
  {accountCode:resolveMap(maps,'ApPayment','Cash'),debitMinor:0,creditMinor:amountMinor},
 ];
 assertBalanced(lines);return lines;
}
/**
 * Phiếu ghi nợ nhà cung cấp: Nợ Phải trả (giảm nợ nhà cung cấp) / Có Thu hồi khiếu nại.
 * Cấn trừ vào hoá đơn sau đó KHÔNG sinh bút toán: hai bên đều nằm trên tài khoản Phải trả.
 */
export function apDebitNoteJournal(maps:GlMap[],amountMinor:number):JournalLineDraft[]{
 if(amountMinor<=0)throw Error('gl_unbalanced');
 const lines:JournalLineDraft[]=[
  {accountCode:resolveMap(maps,'ApDebitNote','Payable'),debitMinor:amountMinor,creditMinor:0},
  {accountCode:resolveMap(maps,'ApDebitNote','ClaimRecovery'),debitMinor:0,creditMinor:amountMinor},
 ];
 assertBalanced(lines);return lines;
}
export function reverseJournal(lines:JournalLineDraft[]):JournalLineDraft[]{
 return lines.map(l=>({accountCode:l.accountCode,debitMinor:l.creditMinor,creditMinor:l.debitMinor}));
}
export function assertBalanced(lines:JournalLineDraft[]):void{
 const debit=lines.reduce((n,l)=>n+l.debitMinor,0),credit=lines.reduce((n,l)=>n+l.creditMinor,0);
 if(!lines.length||debit!==credit||debit<=0)throw Error('gl_unbalanced');
}
export function assertPeriodOpen(status:string|null):void{if(status!=='Open')throw Error('gl_period');}
export function money2(minor:number):string{return minorToAmount(minor,2);}
export function parseMoney2(value:string):number{return amountToMinor(value,2);}
/** Danh sách hoá đơn nhà cung cấp; `supplierId` cho khối chứng từ trên trang chi tiết nhà cung cấp (18/09/2026). */
export const apInvoiceListQuerySchema=receiptListQuerySchema.extend({supplierId:z.string().trim().min(1).max(100).optional()});
export interface AccountingPolicyDto {
 id:string;version:string;status:'Draft'|'Active'|'Obsolete';revision:number;functionalCurrency:string;basis:string;maps:GlMap[];
}
export interface AccountingPeriodDto {id:string;period:string;status:'Open'|'Closed';revision:number;basis:string;}
export interface GlAccountDto {id:string;code:string;name:string;type:string;}
export interface GlJournalDto {
 id:string;documentNo:string;sourceKind:string;sourceId:string|null;period:string;currency:string;status:string;revision:number;memo:string;
 lines:{accountCode:string;debit:string;credit:string}[];
}
export interface ApInvoiceDto {
 id:string;documentNo:string;poId:string;poNumber:string;supplierId:string;supplierName:string;currency:string;invoiceDate:string;
 taxAmount:string;goodsAmount:string;totalAmount:string;status:'Draft'|'Posted'|'Reversed';revision:number;journalId:string|null;
 lines:{id:string;poLineId:string;quantity:string;amount:string}[];
 events:{action:string;reason:string;createdAt:number}[];
}
export interface ApPaymentDto {
 id:string;documentNo:string;supplierId:string;currency:string;paidDate:string;amount:string;status:'Draft'|'Posted'|'Reversed';revision:number;journalId:string|null;
 allocations:{id:string;invoiceId:string;amount:string}[];
}
/** Phiếu ghi nợ nhà cung cấp — tiền là chuỗi 2 số lẻ như hoá đơn / phiếu chi nhà cung cấp. */
export interface ApDebitNoteDto {
 id:string;documentNo:string;supplierId:string;supplierName:string;claimId:string;claimNo:string;
 currency:string;amount:string;appliedAmount:string;unappliedAmount:string;
 /** `YYYY-MM-DD`, có khi đã ghi sổ. */
 noteDate:string|null;status:'Draft'|'Posted'|'Reversed';revision:number;journalId:string|null;
 replacesId:string|null;replacesNo:string|null;
 applications:{id:string;invoiceId:string;invoiceNo:string;amount:string;reason:string;createdAt:number;removedAt:number|null;removedReason:string|null}[];
 events:{action:string;reason:string;createdAt:number}[];
}
/** Hoá đơn nhà cung cấp còn phải trả, để chọn cấn trừ. */
export interface ApOpenInvoiceDto {
 id:string;documentNo:string;poNumber:string;invoiceDate:string;currency:string;total:string;paid:string;deducted:string;remaining:string;
}
